import { Alert, App, Button, Drawer, Empty, Grid, Input, Segmented, Select, Space, Tooltip, Typography } from 'antd'
import { reaction } from 'mobx'
import { observer } from 'mobx-react-lite'
import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
    TbArrowsMove,
    TbCopy,
    TbDownload,
    TbFolderPlus,
    TbFolders,
    TbFolderUp,
    TbInfoCircle,
    TbLayoutGrid,
    TbList,
    TbMenu2,
    TbPencil,
    TbRestore,
    TbSortAscending,
    TbSortDescending,
    TbTrash,
    TbTrashX,
    TbUpload,
    TbUserPlus,
    TbX,
} from 'react-icons/tb'

import { Hint } from '@/Ship/Components/Hint'
import { classNames } from '@/Ship/Helpers/classNames'

import { DISK_SEARCH_MIN_QUERY } from '../Contracts/DiskNode'
import { itemsCount, pluralUk, storageTitle } from '../Helpers/diskFormat'
import { diskTrashUrl } from '../Helpers/diskRoutes'
import { filesFromClipboard, filesFromDrop, filesFromInput, isFileDrag } from '../Helpers/diskUploadSources'
import { DISK_SHARED_TITLE, diskBrowserStore as store } from '../Stores/DiskBrowserStore'
import { diskPropertiesStore } from '../Stores/DiskPropertiesStore'
import { fileViewerStore } from '../Stores/FileViewerStore'
import { uploadQueueStore } from '../Stores/UploadQueueStore'
import { DiskNameModal } from './DiskNameModal'
import { DiskNodeGrid } from './DiskNodeGrid'
import { DiskNodeTable } from './DiskNodeTable'
import { DISK_PROPS_TRIGGER, DiskPropertiesDrawer } from './DiskPropertiesDrawer'
import { DiskShareModal } from './DiskShareModal'
import { DiskSidePanel } from './DiskSidebar'
import { DiskTransferModal } from './DiskTransferModal'
import { DiskTrashTable } from './DiskTrashTable'
import { useDiskActions } from './useDiskActions'

import type { DiskCrumb } from '../Stores/DiskBrowserStore'
import type { DiskSortField } from '../Contracts/DiskNode'
import type { DiskPickedFile, DiskUploadSource, DiskUploadTarget } from '../Contracts/DiskUpload'
import type { DragEvent, ReactNode } from 'react'

import styles from './Disk.module.css'
import uploadStyles from './DiskUpload.module.css'

const { Title } = Typography

const SORT_OPTIONS: { value: DiskSortField; label: string }[] = [
    { value: 'name', label: 'За назвою' },
    { value: 'updatedAt', label: 'За датою зміни' },
    { value: 'size', label: 'За розміром' },
]

/** Один вибраний рядок — те, чиї властивості показати. */
const singleSelected = () => {
    const nodes = store.selectedNodes

    return nodes.length === 1 ? (nodes[0] ?? null) : null
}

/** Клавіша `I`: відкритий дровер — закрити, інакше — властивості вибраного. */
const toggleProperties = (): void => {
    if (diskPropertiesStore.isOpen) {
        diskPropertiesStore.close()

        return
    }

    const node = singleSelected()
    if (node) diskPropertiesStore.open(node)
}

/** Крихта приймає перетягнуті рядки: кинути на предка = перемістити туди («Спільні зі мною» — ні). */
const Crumb = observer(({ crumb }: { crumb: DiskCrumb }) => {
    const navigate = useNavigate()
    const actions = useDiskActions()
    const target = crumb.dropTarget
    const dropProps =
        target === null
            ? {}
            : {
                  onDragOver: (event: DragEvent) => {
                      if (!store.canDropOn(target)) return
                      event.preventDefault()
                      store.setDropTarget(target)
                  },
                  onDragLeave: () => store.setDropTarget(null),
                  onDrop: (event: DragEvent) => {
                      event.preventDefault()
                      void actions.drop(target)
                  },
              }

    return (
        <>
            <button
                type="button"
                className={classNames(styles['crumb'], target !== null && store.dropTarget === target && styles['crumbDrop'])}
                onClick={() => navigate(crumb.url)}
                {...dropProps}
            >
                {crumb.title}
            </button>
            <span aria-hidden>/</span>
        </>
    )
})

/** Шапка вмісту: крихти, назва, лічильник і інструменти. */
interface ContentHeadProps {
    onUpload: () => void
    onUploadFolder: () => void
    compact: boolean
}

const ContentHead = observer(({ onUpload, onUploadFolder, compact }: ContentHeadProps) => {
    const navigate = useNavigate()
    const location = store.location
    const searching = store.isSearchActive
    const crumbs = store.crumbs
    const can = store.folderPermissions

    let title: ReactNode = ''
    let parents: ReactNode = null

    if (searching) {
        title = `Пошук: «${store.searchQuery.trim()}»`
        parents = <span>Пошук за назвою по всіх доступних просторах</span>
    } else if (location?.kind === 'recent') {
        title = 'Нещодавні'
    } else if (location?.kind === 'trash') {
        title = 'Кошик'
    } else if (store.isSharedList) {
        title = DISK_SHARED_TITLE
    } else if (crumbs.length > 0) {
        title = crumbs[crumbs.length - 1]?.title ?? ''
        parents =
            crumbs.length > 1 ? (
                <nav aria-label="Шлях" data-testid="disk-crumbs">
                    {crumbs.slice(0, -1).map((crumb) => (
                        <Crumb key={crumb.key} crumb={crumb} />
                    ))}
                </nav>
            ) : null
    }

    const count = searching ? store.searchTotal : store.total
    const inFolder = store.folderUuid !== null && !searching
    const sortable = searching || inFolder

    return (
        <div className={classNames(styles['head'])}>
            <div className={classNames(styles['headTitles'])}>
                {parents && <div className={classNames(styles['crumbs'])}>{parents}</div>}
                <div className={classNames(styles['titleRow'])}>
                    <Title level={4} className={classNames(styles['title'])} data-testid="disk-title">
                        {title}
                    </Title>
                    {!store.isLoading && <span className={classNames(styles['count'])}>{itemsCount(count)}</span>}
                </div>
            </div>
            <div className={classNames(styles['tools'])}>
                {location?.kind === 'trash' && !searching && (
                    <Select
                        value={location.storageUuid}
                        style={{ minWidth: 200 }}
                        aria-label="Кошик простору"
                        options={store.storages.map((item) => ({ value: item.uuid, label: storageTitle(item) }))}
                        onChange={(uuid: string) => navigate(diskTrashUrl(uuid))}
                    />
                )}
                {inFolder && can.write && (
                    <>
                        <Button icon={<TbFolderPlus />} onClick={() => store.openCreateFolder()}>
                            {compact ? 'Папка' : 'Створити папку'}
                        </Button>
                        <Tooltip title={compact ? 'Завантажити папку' : ''}>
                            <Button icon={<TbFolderUp />} onClick={onUploadFolder} aria-label="Завантажити папку">
                                {compact ? null : 'Завантажити папку'}
                            </Button>
                        </Tooltip>
                        <Button type="primary" icon={<TbUpload />} onClick={onUpload}>
                            Завантажити
                        </Button>
                    </>
                )}
                {sortable && (
                    <Space.Compact>
                        <Select
                            value={store.sort.field}
                            options={SORT_OPTIONS}
                            style={{ width: 160 }}
                            aria-label="Сортування"
                            onChange={(field: DiskSortField) => store.setSort({ field, order: store.sort.order })}
                        />
                        <Tooltip title={store.sort.order === 'asc' ? 'За зростанням' : 'За спаданням'}>
                            <Button
                                icon={store.sort.order === 'asc' ? <TbSortAscending /> : <TbSortDescending />}
                                aria-label={store.sort.order === 'asc' ? 'За зростанням' : 'За спаданням'}
                                onClick={() =>
                                    store.setSort({
                                        field: store.sort.field,
                                        order: store.sort.order === 'asc' ? 'desc' : 'asc',
                                    })
                                }
                            />
                        </Tooltip>
                    </Space.Compact>
                )}
                {location?.kind !== 'trash' && (
                    <Segmented
                        value={store.view}
                        onChange={(value) => store.setView(value === 'grid' ? 'grid' : 'list')}
                        options={[
                            { value: 'list', icon: <TbList />, title: 'Список' },
                            { value: 'grid', icon: <TbLayoutGrid />, title: 'Плитка' },
                        ]}
                        aria-label="Вигляд"
                    />
                )}
            </div>
        </div>
    )
})

/** Панель дій над вибраним — лише те, на що є права в усіх вибраних. */
const SelectionBar = observer(({ compact }: { compact: boolean }) => {
    const actions = useDiskActions()
    const nodes = store.selectedNodes
    /** На телефоні — лише іконки з підписом у підказці, щоб дії влізли в рядок. */
    const action = (label: string, icon: ReactNode, onClick: () => void, danger = false) => (
        <Tooltip title={compact ? label : ''}>
            <Button
                icon={icon}
                danger={danger}
                onClick={onClick}
                aria-label={label}
                {...(label === 'Властивості' ? { [DISK_PROPS_TRIGGER]: '' } : {})}
            >
                {compact ? null : label}
            </Button>
        </Tooltip>
    )

    if (nodes.length === 0) return null

    const all = (key: 'read' | 'update' | 'delete' | 'manage') => nodes.every((node) => node.permissions[key] === true)
    const single = nodes.length === 1 ? nodes[0] : undefined
    const inTrash = store.location?.kind === 'trash' && !store.isSearchActive

    return (
        <div className={classNames(styles['selectionBar'])} role="toolbar" aria-label="Дії з вибраним">
            <span className={classNames(styles['selectionText'])}>
                {compact ? nodes.length : `Вибрано: ${nodes.length}`}
            </span>
            {inTrash ? (
                <>
                    {all('delete') && action('Відновити', <TbRestore />, () => void actions.restore(nodes))}
                    {all('manage') && action('Видалити назавжди', <TbTrashX />, () => actions.purge(nodes), true)}
                </>
            ) : (
                <>
                    {single &&
                        single.type === 'file' &&
                        all('read') &&
                        action('Скачати', <TbDownload />, () => void actions.download(single))}
                    {single &&
                        action(single.permissions.share ? 'Поділитися' : 'Хто має доступ', <TbUserPlus />, () =>
                            actions.share(single)
                        )}
                    {single && action('Властивості', <TbInfoCircle />, () => diskPropertiesStore.open(single))}
                    {single && all('update') && action('Перейменувати', <TbPencil />, () => store.openRename(single))}
                    {all('update') && action('Перемістити', <TbArrowsMove />, () => store.openTransfer('move', nodes))}
                    {all('read') && action('Копіювати', <TbCopy />, () => store.openTransfer('copy', nodes))}
                    {all('delete') && action('У кошик', <TbTrash />, () => void actions.trash(nodes), true)}
                </>
            )}
            <Button
                type="text"
                icon={<TbX />}
                className={classNames(styles['selectionClear'])}
                onClick={() => store.clearSelection()}
                aria-label="Зняти вибір"
            >
                {compact ? null : 'Зняти вибір'}
            </Button>
        </div>
    )
})

/** Підказки — лише там, де правило стосується того, що на екрані. */
const ContentHints = observer(() => {
    const location = store.location
    const storage = store.currentStorage

    if (store.isSharedList) {
        return (
            <Hint id="disk.shared">
                Тут папки й файли, якими з вами поділились, — найвищі в дереві. Відкрита спільна папка показує все
                всередині, а папок вище за неї не видно.
            </Hint>
        )
    }

    if (store.isSearchActive || storage === null) return null

    if (location?.kind === 'trash') {
        const days = storage.settings?.trashDays

        return (
            <>
                {days !== undefined && days > 0 && (
                    <Hint id="disk.trash">
                        Видалене лежить у кошику {days} {pluralUk(days, 'день', 'дні', 'днів')}, потім зникає назавжди.
                        «Відновити» повертає в ту саму папку, а якщо її вже немає чи вона теж у кошику — у корінь
                        простору.
                    </Hint>
                )}
                <Hint id="disk.quota">
                    Квоту займають і файли в кошику, і старі версії файлів. Файл із кошика звільняє місце лише після
                    «Видалити назавжди» або автоочищення.
                </Hint>
            </>
        )
    }

    if (location?.kind === 'folder' && storage.type === 'company' && location.folderUuid === null) {
        return (
            <Hint id="disk.company">
                Вміст «Компанії» бачать усі співробітники. Додавати й змінювати тут файли за замовчуванням може лише
                адміністратор.
            </Hint>
        )
    }

    return null
})

interface ContentBodyProps {
    compact: boolean
    /** Планшетна ширина: колонку «Автор» ховаємо, щоб назві лишилось місце. */
    narrow: boolean
    onUpload: () => void
}

const ContentBody = observer(({ compact, narrow, onUpload }: ContentBodyProps) => {
    const location = store.location
    const searching = store.isSearchActive
    const error = searching ? store.searchError : store.error
    const loading = searching ? store.isSearching : store.isLoading
    const empty = !loading && store.visibleItems.length === 0

    if (error !== null) {
        return (
            <Alert
                type="error"
                showIcon
                title={error}
                action={
                    <Button size="small" onClick={() => void store.reload()}>
                        Повторити
                    </Button>
                }
                data-testid="disk-error"
            />
        )
    }

    if (empty) {
        let text = 'Папка порожня'
        let extra: ReactNode = null

        if (searching) text = 'Нічого не знайдено'
        else if (location?.kind === 'recent') text = 'Тут з’являться файли, з якими ви нещодавно працювали'
        else if (location?.kind === 'trash') text = 'Кошик порожній'
        else if (store.isSharedList) text = 'З вами ще нічим не поділились'
        else if (store.folderPermissions.write) {
            extra = (
                <Space wrap style={{ justifyContent: 'center' }}>
                    <Button icon={<TbFolderPlus />} onClick={() => store.openCreateFolder()}>
                        Створити папку
                    </Button>
                    <Button type="primary" icon={<TbUpload />} onClick={onUpload}>
                        Завантажити
                    </Button>
                </Space>
            )
        }

        return (
            <div className={classNames(styles['empty'])}>
                <Empty description={text} data-testid="disk-empty">
                    {extra}
                </Empty>
            </div>
        )
    }

    if (location?.kind === 'trash' && !searching) return <DiskTrashTable compact={compact} />

    return store.view === 'grid' ? (
        <DiskNodeGrid />
    ) : (
        <DiskNodeTable
            compact={compact}
            narrow={narrow}
            sortable={searching || (store.folderUuid !== null && location?.kind !== 'trash')}
            showLocation={searching || location?.kind === 'recent' || store.isSharedList}
        />
    )
})

/**
 * Екран «Диск» (`DISK-001` §6, наряд Д1.9): ліворуч розділи з деревом і
 * квотою, праворуч — вміст відкритого місця; пошук по диску в шапці.
 *
 * Вигляд і взаємодію взято з референсу (`DriveContainer`): дерево, список і
 * плитка, крихти, панель вибору, модалки назви й переміщення, картка квоти.
 * На вузькій ширині панель розділів ховається в шухляду, таблиця стає
 * однією колонкою з підписом під назвою.
 *
 * Завантаження (Д1.10) — у глобальну чергу `UploadQueueStore`: кнопки
 * «Завантажити» й «Завантажити папку», перетягування файлів і папок з ОС на
 * вміст, вставка з буфера (Ctrl+V). Поступ показує панель менеджера в каркасі.
 */
export const DiskScreen = observer(() => {
    const { message } = App.useApp()
    const screens = Grid.useBreakpoint()
    const wide = screens.lg !== false
    const compact = screens.md === false
    const narrow = screens.xl === false
    const { pathname } = useLocation()
    const [panelOpen, setPanelOpen] = useState(false)
    const [dragOver, setDragOver] = useState(false)
    const dragDepth = useRef(0)
    const fileInput = useRef<HTMLInputElement>(null)
    const actions = useDiskActions()
    // Обробник клавіш ставиться раз — дія «Поділитися» береться з ref
    const shareRef = useRef(actions.share)
    shareRef.current = actions.share
    const folderInput = useRef<HTMLInputElement>(null)

    useEffect(() => {
        document.title = 'Диск'
        void store.loadQuota()
    }, [])

    // Перехід у шухляді закриває її; дровер властивостей — теж (інша папка)
    useEffect(() => {
        setPanelOpen(false)
        diskPropertiesStore.close()
    }, [pathname])

    // Дровер властивостей іде за вибором: інший рядок — інші властивості; рядок
    // зник зі списку (у кошик, переміщено, пошук змінився) — дровер закривається
    useEffect(() => {
        const follow = reaction(singleSelected, (node) => {
            if (node) diskPropertiesStore.follow(node)
        })
        const vanish = reaction(
            () => (store.isLoading || store.isSearching ? null : store.visibleItems.map((node) => node.uuid)),
            (uuids) => {
                const shown = diskPropertiesStore.uuid
                if (uuids !== null && shown !== null && !uuids.includes(shown)) diskPropertiesStore.close()
            }
        )

        return () => {
            follow()
            vanish()
            diskPropertiesStore.close()
        }
    }, [])

    // `I` — властивості вибраного, Esc — закрити дровер (фокус лишається в
    // списку, тож власний Esc дровера його не чує). Не з полів введення,
    // модалок і меню й не під переглядачем — у них свої клавіші
    useEffect(() => {
        const onKey = (event: KeyboardEvent): void => {
            const target = event.target as HTMLElement | null
            const inField = target?.closest('input, textarea, select, [contenteditable="true"], .ant-modal, .ant-dropdown')

            // Ctrl/Cmd+Shift+S — «Поділитися» вибраним (як у Google Drive — один елемент)
            if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.code === 'KeyS' && !inField) {
                const node = singleSelected()
                if (node && !fileViewerStore.isOpen) {
                    event.preventDefault()
                    shareRef.current(node)
                }

                return
            }

            if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey) return
            if (inField) return
            if (fileViewerStore.isOpen) return

            if (event.code === 'KeyI') {
                event.preventDefault()
                toggleProperties()
            } else if (event.key === 'Escape' && diskPropertiesStore.isOpen) {
                event.preventDefault()
                diskPropertiesStore.close()
            }
        }

        document.addEventListener('keydown', onKey)

        return () => document.removeEventListener('keydown', onKey)
    }, [])

    // `webkitdirectory` React не знає як атрибут — ставимо властивість елемента
    useEffect(() => {
        if (folderInput.current) folderInput.current.webkitdirectory = true
    }, [])

    /** Куди вантажити: відкрита папка з правом запису; інакше — нікуди. */
    const uploadTarget = (): DiskUploadTarget | null => {
        const parent = store.folderUuid
        const storage = store.currentStorage

        if (parent === null || store.isSearchActive || store.folderPermissions.write !== true) return null

        return {
            parent,
            parentName: store.crumbs[store.crumbs.length - 1]?.title ?? (storage ? storageTitle(storage) : ''),
            storageUuid: storage?.uuid ?? null,
        }
    }

    const enqueue = (picked: DiskPickedFile[], source: DiskUploadSource): void => {
        const target = uploadTarget()

        if (picked.length === 0) return
        if (target === null) {
            void message.warning('Відкрийте папку, у яку можна додавати файли')

            return
        }

        void uploadQueueStore.enqueue(picked, target, source)
    }

    // Ctrl+V: файли й зображення з буфера — у відкриту папку (не з полів введення)
    useEffect(() => {
        const onPaste = (event: ClipboardEvent): void => {
            const target = event.target as HTMLElement | null

            if (target?.closest('input, textarea, [contenteditable="true"]')) return

            const picked = filesFromClipboard(event.clipboardData, new Date())
            if (picked.length === 0) return

            event.preventDefault()
            enqueue(picked, 'files')
        }

        document.addEventListener('paste', onPaste)

        return () => document.removeEventListener('paste', onPaste)
    })

    const startUpload = (): void => fileInput.current?.click()
    const startFolderUpload = (): void => folderInput.current?.click()

    const resetDrag = (): void => {
        dragDepth.current = 0
        setDragOver(false)
    }

    /** Перетягування з ОС: лише файли (рядки диска тягнуть переміщення) і лише в папку з правом запису. */
    const dropHandlers = {
        onDragEnter: (event: DragEvent) => {
            if (!isFileDrag(event.dataTransfer) || uploadTarget() === null) return
            event.preventDefault()
            dragDepth.current += 1
            setDragOver(true)
        },
        onDragOver: (event: DragEvent) => {
            if (!isFileDrag(event.dataTransfer) || uploadTarget() === null) return
            event.preventDefault()
            event.dataTransfer.dropEffect = 'copy'
        },
        onDragLeave: (event: DragEvent) => {
            if (!isFileDrag(event.dataTransfer)) return
            dragDepth.current = Math.max(0, dragDepth.current - 1)
            if (dragDepth.current === 0) setDragOver(false)
        },
        onDrop: (event: DragEvent) => {
            if (!isFileDrag(event.dataTransfer)) return
            event.preventDefault()
            resetDrag()
            if (uploadTarget() === null) return

            void filesFromDrop(event.dataTransfer).then(
                (picked) => enqueue(picked, 'files'),
                () => void message.error('Не вдалося прочитати перетягнуті файли')
            )
        },
    }

    return (
        <div className={classNames(styles['page'])}>
            <div className={classNames(styles['hero'])}>
                {!wide && (
                    <Button icon={<TbMenu2 />} onClick={() => setPanelOpen(true)} aria-label="Розділи диска">
                        Розділи
                    </Button>
                )}
                <Title level={3} className={classNames(styles['heroTitle'])}>
                    <TbFolders aria-hidden />
                    Диск
                </Title>
                <Input.Search
                    className={classNames(styles['search'])}
                    allowClear
                    placeholder={`Пошук по диску — від ${DISK_SEARCH_MIN_QUERY} символів`}
                    value={store.searchQuery}
                    onChange={(event) => store.setSearchQuery(event.target.value)}
                    onSearch={(value) => store.setSearchQuery(value)}
                    aria-label="Пошук по диску"
                    data-testid="disk-search"
                />
            </div>

            <div className={classNames(styles['layout'])}>
                {wide && (
                    <aside className={classNames(styles['side'])}>
                        <DiskSidePanel />
                    </aside>
                )}
                <section
                    className={classNames(styles['panel'])}
                    aria-label="Вміст"
                    data-testid="disk-content"
                    {...dropHandlers}
                >
                    {dragOver && (
                        <div className={classNames(uploadStyles['dropZone'])} data-testid="disk-drop-zone">
                            <span>
                                <TbUpload aria-hidden />
                                Відпустіть, щоб завантажити в «{uploadTarget()?.parentName ?? ''}»
                            </span>
                        </div>
                    )}
                    {store.storagesError !== null ? (
                        <div className={classNames(styles['body'])}>
                            <Alert
                                type="error"
                                showIcon
                                title={store.storagesError}
                                action={
                                    <Button
                                        size="small"
                                        onClick={() => void store.loadStorages(true).then(() => store.reload())}
                                    >
                                        Повторити
                                    </Button>
                                }
                            />
                        </div>
                    ) : (
                        <>
                            <div className={classNames(styles['headWrap'])}>
                                <ContentHead onUpload={startUpload} onUploadFolder={startFolderUpload} compact={compact} />
                                <SelectionBar compact={compact} />
                            </div>
                            <div className={classNames(styles['body'])}>
                                <ContentHints />
                                <ContentBody compact={compact} narrow={narrow} onUpload={startUpload} />
                            </div>
                        </>
                    )}
                </section>
            </div>

            <input
                ref={fileInput}
                type="file"
                multiple
                hidden
                onChange={(event) => {
                    enqueue(filesFromInput(event.target.files), 'files')
                    event.target.value = ''
                }}
                data-testid="disk-file-input"
            />
            <input
                ref={folderInput}
                type="file"
                multiple
                hidden
                onChange={(event) => {
                    enqueue(filesFromInput(event.target.files), 'folder')
                    event.target.value = ''
                }}
                data-testid="disk-folder-input"
            />

            {!wide && (
                <Drawer
                    placement="left"
                    open={panelOpen}
                    onClose={() => setPanelOpen(false)}
                    size={Math.min(320, window.innerWidth)}
                    title="Розділи диска"
                >
                    <div className={classNames(styles['side'])}>
                        <DiskSidePanel />
                    </div>
                </Drawer>
            )}

            <DiskNameModal />
            <DiskTransferModal />
            <DiskPropertiesDrawer compact={compact} />
            <DiskShareModal />
        </div>
    )
})
